/*
  Soul Sun cart drawer content (snippets/cart-drawer.liquid + snippets/ss-cart-drawer-content.liquid).

  Opening and closing stay with theme.Drawers / theme.CartDrawer in theme.js ('cart:open' and
  'cart:close'). This element owns what is inside: quantities, removal, suggestions, shipping
  quotes and the free shipping bar. Every cart change asks Shopify for sections/ss-cart-drawer
  (bundled section rendering) and swaps the [data-ss-region] blocks from that render.
*/
(() => {
  if (customElements.get('ss-cart-drawer')) return;

  const SECTION = 'ss-cart-drawer';
  const ZIP_KEY = 'ss-cart:zip';
  const QTY_DELAY = 250;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  // Shopify needs the state (province) to quote a Brazilian address; it follows from the CEP.
  const UF_BY_CEP = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
    [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
    [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
    [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
    [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
    [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
    [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];

  const ufFromCep = (digits) => {
    const prefix = parseInt(digits.slice(0, 5), 10);
    const range = UF_BY_CEP.find(([from, to]) => prefix >= from && prefix <= to);
    return range ? range[2] : '';
  };

  const formatCep = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class SsCartDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('#CartDrawer');
      this.queue = Promise.resolve();
      this.qtyTimers = {};
      this.shippingRates = null;
      this.progressShown = false;
      this.stale = false;
      this.cartTotal = this.readMeta().total;

      this.addEventListener('click', (evt) => this.onClick(evt));
      this.addEventListener('change', (evt) => this.onChange(evt));
      this.addEventListener('input', (evt) => this.onInput(evt));
      this.addEventListener('keydown', (evt) => this.onKeydown(evt));

      document.addEventListener('ajaxProduct:added', (evt) => this.onProductAdded(evt));
      document.addEventListener('cart:build', () => this.enqueue(() => this.refresh()));
      document.addEventListener('drawerOpen.CartDrawer', () => this.onOpen());
      window.addEventListener('pageshow', (evt) => { if (evt.persisted) this.stale = true; });
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.stale = true; });

      const zip = this.zipInput;
      const saved = sessionStorage.getItem(ZIP_KEY);
      if (zip && saved) zip.value = saved;

      this.lockedScrollY = null;
      if (this.drawer) {
        new MutationObserver(() => this.syncPageLock()).observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      this.bindUpsell();
    }

    // iOS Safari ignores overflow:hidden on <html>/<body>, so the page behind the open drawer kept
    // scrolling (and the toolbar resized). Pin the body while the cart is open, then restore the
    // exact scroll position when it closes.
    syncPageLock() {
      const open = this.drawer.classList.contains('drawer--is-open');
      const body = document.body.style;
      if (open && this.lockedScrollY === null) {
        this.lockedScrollY = window.scrollY;
        body.position = 'fixed';
        body.top = `-${this.lockedScrollY}px`;
        body.left = '0';
        body.right = '0';
        body.width = '100%';
      } else if (!open && this.lockedScrollY !== null) {
        const y = this.lockedScrollY;
        this.lockedScrollY = null;
        body.position = '';
        body.top = '';
        body.left = '';
        body.right = '';
        body.width = '';
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      }
    }

    get zipInput() {
      return this.querySelector('[data-ss-zip]');
    }

    readMeta(scope = this) {
      const meta = scope.querySelector('[data-ss-region="meta"]');
      return {
        count: meta ? parseInt(meta.dataset.count, 10) || 0 : 0,
        total: meta ? parseInt(meta.dataset.total, 10) || 0 : 0,
        empty: meta ? meta.dataset.empty === 'true' : true,
        signature: meta ? meta.dataset.signature || '' : '',
      };
    }

    enqueue(task) {
      this.queue = this.queue.then(task, task).catch((err) => console.error(err));
      return this.queue;
    }

    /* ---------- events ---------- */

    onProductAdded(evt) {
      const product = evt.detail && evt.detail.product;
      document.dispatchEvent(new CustomEvent('cart:open'));
      this.bumpHeaderIcon();
      this.enqueue(() => this.refresh({ highlightKey: product && product.key }));
    }

    onOpen() {
      if (this.stale) {
        this.stale = false;
        this.enqueue(() => this.refresh());
      } else {
        this.enqueue(() => this.refreshIfChanged());
      }
      if (!this.progressShown) {
        this.progressShown = true;
        this.playProgressIntro();
      }
      setTimeout(() => this.updateUpsellNav(), 60);
      const zip = this.zipInput;
      if (zip && zip.value && !this.shippingRates && !this.readMeta().empty) {
        this.calculateShipping({ quiet: true });
      }
    }

    onClick(evt) {
      const summary = evt.target.closest('.ss-ship__summary');
      if (summary) {
        evt.preventDefault();
        this.toggleShipping(summary.closest('[data-ss-ship]'));
        return;
      }

      const qtyBtn = evt.target.closest('[data-ss-qty]');
      if (qtyBtn) {
        const line = qtyBtn.closest('[data-ss-line]');
        const valueEl = line.querySelector('.ss-qty__value');
        const quantity = Math.max(0, (parseInt(valueEl.textContent, 10) || 0) + parseInt(qtyBtn.dataset.ssQty, 10));
        valueEl.textContent = quantity;
        this.scheduleQuantity(line, quantity);
        return;
      }

      const removeBtn = evt.target.closest('[data-ss-remove]');
      if (removeBtn) {
        const line = removeBtn.closest('[data-ss-line]');
        clearTimeout(this.qtyTimers[line.dataset.key]);
        line.classList.add('is-removing');
        this.enqueue(() => this.changeQuantity(line.dataset.key, 0, line));
        return;
      }

      const addBtn = evt.target.closest('[data-ss-upsell-add]');
      if (addBtn) {
        this.addSuggestion(addBtn.closest('[data-ss-upsell-card]'));
        return;
      }

      const arrow = evt.target.closest('[data-ss-upsell-prev], [data-ss-upsell-next]');
      if (arrow) {
        this.scrollSuggestions(arrow.hasAttribute('data-ss-upsell-next') ? 1 : -1);
        return;
      }

      if (evt.target.closest('[data-ss-ship-calc]')) {
        this.calculateShipping();
        return;
      }

      if (evt.target.closest('[data-ss-continue]')) {
        document.dispatchEvent(new CustomEvent('cart:close'));
      }
    }

    onChange(evt) {
      if (evt.target.matches('select[data-ss-upsell-variant]')) {
        const option = evt.target.selectedOptions[0];
        const price = evt.target.closest('[data-ss-upsell-card]').querySelector('[data-ss-upsell-price]');
        if (option && price) {
          price.innerHTML = `${option.dataset.compare ? `<s>${option.dataset.compare}</s> ` : ''}<span>${option.dataset.price}</span>`;
        }
        return;
      }
    }

    onInput(evt) {
      if (evt.target.matches('[data-ss-zip]')) {
        const formatted = formatCep(evt.target.value);
        if (formatted !== evt.target.value) evt.target.value = formatted;
      }
    }

    onKeydown(evt) {
      // Enter inside the drawer form would submit it, i.e. go to checkout.
      if (evt.key === 'Enter' && evt.target.matches('[data-ss-zip]')) {
        evt.preventDefault();
        this.calculateShipping();
      }
    }

    /* ---------- cart requests ---------- */

    async refresh(options = {}) {
      const response = await fetch(`${root}?sections=${SECTION}`, { credentials: 'same-origin' });
      const sections = await response.json();
      if (sections && sections[SECTION]) await this.render(sections[SECTION], options);
    }

    // Apps (e.g. shoppable videos) can change the cart without telling the theme.
    async refreshIfChanged() {
      const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((response) => response.json());
      const signature = cart.items.map((item) => `${item.key}:${item.quantity};`).join('');
      if (signature !== this.readMeta().signature || cart.total_price !== this.cartTotal) await this.refresh();
    }

    async mutate(url, body, options = {}) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw data;

      if (options.highlightFromResponse && Array.isArray(data.items) && data.items[0]) {
        options = { ...options, highlightKey: data.items[0].key };
      }
      const html = data.sections && data.sections[SECTION];
      if (html) {
        await this.render(html, options);
      } else {
        await this.refresh(options);
      }
      return data;
    }

    scheduleQuantity(line, quantity) {
      const key = line.dataset.key;
      line.classList.add('is-pending');
      if (quantity === 0) line.classList.add('is-removing');
      clearTimeout(this.qtyTimers[key]);
      this.qtyTimers[key] = setTimeout(() => {
        this.enqueue(() => this.changeQuantity(key, quantity, line));
      }, QTY_DELAY);
    }

    async changeQuantity(key, quantity, line) {
      if (line) line.classList.add('is-busy');
      try {
        const cart = await this.mutate(`${root}cart/change.js`, { id: key, quantity });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        await this.refresh();
        this.showLineError(key, (error && (error.description || error.message)) || 'Não foi possível atualizar a quantidade.');
      }
    }

    addSuggestion(card) {
      if (!card || card.classList.contains('is-adding')) return;
      const variantInput = card.querySelector('[data-ss-upsell-variant]');
      const id = variantInput && parseInt(variantInput.value, 10);
      if (!id) return;
      card.classList.add('is-adding');

      this.enqueue(async () => {
        try {
          const data = await this.mutate(`${root}cart/add.js`, { items: [{ id, quantity: 1 }] }, {
            beforeRender: async () => {
              card.classList.remove('is-adding');
              card.classList.add('is-added');
              await sleep(reducedMotion.matches ? 0 : 420);
            },
            highlightFromResponse: true,
          });
          this.bumpHeaderIcon();
          return data;
        } catch (error) {
          card.classList.remove('is-adding');
          const message = (error && (error.description || error.message)) || 'Não foi possível adicionar.';
          window.alert(message);
        }
      });
    }

    /* ---------- rendering ---------- */

    async render(html, options = {}) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (options.beforeRender) await options.beforeRender();

      const focus = this.captureFocus();
      const meta = this.readMeta(doc);

      doc.querySelectorAll('[data-ss-region]').forEach((next) => {
        const name = next.dataset.ssRegion;
        if (name === 'shipping') return;
        const current = this.querySelector(`[data-ss-region="${name}"]`);
        if (!current) return;
        if (name === 'progress') {
          this.updateProgress(current, next);
          return;
        }
        current.replaceWith(next);
      });

      this.cartTotal = meta.total;
      this.classList.toggle('is-empty', meta.empty);
      this.updateCount(meta.count);
      this.bindUpsell();
      this.restoreFocus(focus);

      if (options.highlightKey) this.highlightLine(options.highlightKey);

      if (meta.empty) {
        this.shippingRates = null;
        const result = this.querySelector('[data-ss-ship-result]');
        if (result) result.innerHTML = '';
      } else if (this.shippingRates || (this.zipInput && this.zipInput.value && this.drawer.classList.contains('drawer--is-open'))) {
        clearTimeout(this.shippingTimer);
        this.shippingTimer = setTimeout(() => this.calculateShipping({ quiet: true }), 400);
      }
    }

    updateCount(count) {
      const countEl = this.drawer && this.drawer.querySelector('[data-ss-count]');
      if (countEl) {
        countEl.textContent = count;
        countEl.hidden = count === 0;
      }
      document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
        bubble.classList.toggle('cart-link__bubble--visible', count > 0);
      });
    }

    highlightLine(key) {
      const line = this.querySelector(`[data-ss-line][data-key="${CSS.escape(key)}"]`);
      if (!line) return;
      line.classList.add('is-new');
      setTimeout(() => line.classList.remove('is-new'), 1600);
    }

    showLineError(key, message) {
      const line = this.querySelector(`[data-ss-line][data-key="${CSS.escape(key)}"]`);
      const error = line && line.querySelector('[data-ss-line-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      setTimeout(() => { error.hidden = true; }, 5000);
    }

    captureFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return null;
      const line = active.closest('[data-ss-line]');
      return {
        key: line ? line.dataset.key : null,
        qty: active.dataset.ssQty || null,
        upsell: !!active.closest('[data-ss-upsell]'),
      };
    }

    restoreFocus(focus) {
      if (!focus || this.contains(document.activeElement)) return;
      let target = null;
      if (focus.key) {
        const line = this.querySelector(`[data-ss-line][data-key="${CSS.escape(focus.key)}"]`);
        if (line) target = line.querySelector(focus.qty ? `[data-ss-qty="${focus.qty}"]` : '[data-ss-remove]');
      }
      if (!target && focus.upsell) target = this.querySelector('[data-ss-upsell-add]');
      if (!target) target = this.drawer.querySelector('.ss-cart__close');
      if (target) target.focus({ preventScroll: true });
    }

    bumpHeaderIcon() {
      if (reducedMotion.matches) return;
      document.querySelectorAll('.js-drawer-open-cart .cart-link').forEach((icon) => {
        icon.classList.remove('ss-bump');
        void icon.offsetWidth;
        icon.classList.add('ss-bump');
      });
    }

    /* ---------- free shipping bar ---------- */

    playProgressIntro() {
      const bar = this.querySelector('.ss-progress');
      if (!bar || reducedMotion.matches) return;
      const value = parseFloat(bar.dataset.progress) || 0;
      if (value <= 0) return;
      const complete = bar.dataset.complete === 'true';
      bar.classList.add('is-instant');
      bar.classList.remove('is-complete');
      bar.dataset.complete = 'false';
      bar.style.setProperty('--ss-p', 0);
      void bar.offsetWidth;
      bar.classList.remove('is-instant');
      setTimeout(() => {
        bar.dataset.complete = complete;
        this.moveProgress(bar, value, 0, complete);
      }, 220);
    }

    updateProgress(current, next) {
      const bar = current.querySelector('.ss-progress');
      const nextBar = next.querySelector('.ss-progress');
      if (!bar || !nextBar) {
        current.replaceWith(next);
        return;
      }

      const from = parseFloat(bar.dataset.progress) || 0;
      const to = parseFloat(nextBar.dataset.progress) || 0;
      const complete = nextBar.dataset.complete === 'true';

      const message = bar.querySelector('.ss-progress__message');
      const nextMessage = nextBar.querySelector('.ss-progress__message');
      if (message && nextMessage && message.innerHTML !== nextMessage.innerHTML) {
        message.classList.add('is-swapping');
        setTimeout(() => {
          message.innerHTML = nextMessage.innerHTML;
          message.classList.remove('is-swapping');
        }, reducedMotion.matches ? 0 : 180);
      }

      const track = bar.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', Math.round(to * 100));
      bar.dataset.progress = to;
      bar.dataset.complete = complete;
      this.moveProgress(bar, to, from, complete);
    }

    moveProgress(bar, to, from, complete) {
      bar.style.setProperty('--ss-p', to);
      const animate = !reducedMotion.matches && to !== from;
      // When the goal is reached the cart rides into the goal (CSS: [data-complete]) and the
      // check appears as it arrives.
      const arriving = animate && complete && from < 1;
      bar.classList.toggle('is-complete', complete && !arriving);
      if (!animate) return;

      bar.classList.toggle('is-reversing', to < from);
      bar.classList.add('is-moving');
      clearTimeout(this.movingTimer);
      this.movingTimer = setTimeout(() => bar.classList.remove('is-moving', 'is-reversing'), 1000);

      clearTimeout(this.celebrateTimer);
      bar.classList.remove('is-celebrating');
      if (arriving) {
        this.celebrateTimer = setTimeout(() => {
          bar.classList.add('is-complete', 'is-celebrating');
          this.celebrateTimer = setTimeout(() => bar.classList.remove('is-celebrating'), 1400);
        }, 800);
      }
    }

    /* ---------- suggestions carousel ---------- */

    bindUpsell() {
      const track = this.querySelector('[data-ss-upsell-track]');
      if (!track || track.dataset.bound) return;
      track.dataset.bound = 'true';
      let frame = null;
      track.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          this.updateUpsellNav();
        });
      }, { passive: true });
      this.updateUpsellNav();
    }

    // Dots and arrows follow the scroll position; the arrows stop at both ends.
    updateUpsellNav() {
      const track = this.querySelector('[data-ss-upsell-track]');
      const cards = track ? track.querySelectorAll('[data-ss-upsell-card]') : [];
      if (!cards.length || !track.clientWidth) return;
      const max = track.scrollWidth - track.clientWidth;
      const step = cards[0].offsetWidth + this.gap(track);
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= max - 2;
      const index = atEnd ? cards.length - 1 : Math.min(cards.length - 1, Math.round(track.scrollLeft / step));
      this.querySelectorAll('.ss-upsell__dot').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      const prev = this.querySelector('[data-ss-upsell-prev]');
      const next = this.querySelector('[data-ss-upsell-next]');
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    gap(track) {
      return parseFloat(getComputedStyle(track).columnGap) || 0;
    }

    scrollSuggestions(direction) {
      const track = this.querySelector('[data-ss-upsell-track]');
      const card = track && track.querySelector('[data-ss-upsell-card]');
      if (!card) return;
      const step = card.offsetWidth + this.gap(track);
      const max = track.scrollWidth - track.clientWidth;
      const target = Math.min(max, Math.max(0, track.scrollLeft + direction * step));
      track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    /* ---------- shipping quote ---------- */

    // <details> opens instantly; animate the body's height so the footer grows smoothly.
    toggleShipping(details) {
      const body = details && details.querySelector('.ss-ship__body');
      if (!body) return;
      const opening = !details.classList.contains('is-open');
      details.classList.toggle('is-open', opening);
      if (this.shipAnimation) this.shipAnimation.cancel();

      if (reducedMotion.matches) {
        details.open = opening;
        return;
      }
      if (opening) details.open = true;
      const full = body.scrollHeight;
      const padding = getComputedStyle(body).paddingBottom;
      const shown = { height: `${full}px`, paddingBottom: padding, opacity: 1 };
      const hidden = { height: '0px', paddingBottom: '0px', opacity: 0 };
      body.style.overflow = 'hidden';
      const animation = body.animate(opening ? [hidden, shown] : [shown, hidden], {
        duration: opening ? 300 : 240,
        easing: 'cubic-bezier(.4, 0, .2, 1)',
      });
      this.shipAnimation = animation;
      const done = () => {
        body.style.overflow = '';
        if (this.shipAnimation === animation) this.shipAnimation = null;
      };
      animation.onfinish = () => {
        if (!opening) details.open = false;
        done();
      };
      animation.oncancel = done;
    }

    async calculateShipping({ quiet = false } = {}) {
      const input = this.zipInput;
      const result = this.querySelector('[data-ss-ship-result]');
      if (!input || !result) return;

      const digits = input.value.replace(/\D/g, '');
      if (digits.length !== 8) {
        if (!quiet) this.showShippingMessage('Digite um CEP válido, com 8 números.', true);
        return;
      }

      const zip = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      const params = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': ufFromCep(digits),
      });
      const ticket = (this.shippingTicket = (this.shippingTicket || 0) + 1);
      const button = this.querySelector('[data-ss-ship-calc]');
      if (button) button.classList.add('is-loading');
      result.classList.add('is-loading');

      try {
        const prepare = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST', credentials: 'same-origin' });
        if (!prepare.ok) throw new Error('prepare');
        let rates = null;
        for (let attempt = 0; attempt < 12 && !rates; attempt++) {
          const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, { credentials: 'same-origin' });
          const data = response.ok ? await response.json() : null;
          if (data && data.shipping_rates) rates = data.shipping_rates;
          else await sleep(700);
        }
        if (ticket !== this.shippingTicket) return;
        if (!rates) throw new Error('timeout');

        sessionStorage.setItem(ZIP_KEY, zip);
        this.renderRates(rates);
      } catch (error) {
        if (ticket !== this.shippingTicket) return;
        this.shippingRates = null;
        this.showShippingMessage('Não encontramos opções de entrega para esse CEP. Confira os números e tente de novo.', true);
      } finally {
        if (ticket === this.shippingTicket) {
          if (button) button.classList.remove('is-loading');
          result.classList.remove('is-loading');
        }
      }
    }

    renderRates(rates) {
      const result = this.querySelector('[data-ss-ship-result]');
      const note = this.querySelector('[data-ss-ship-note]');
      const sorted = rates
        .map((rate) => ({ name: rate.name, cents: Math.round(parseFloat(rate.price) * 100) || 0, days: rate.delivery_days || [] }))
        .sort((a, b) => a.cents - b.cents);

      if (!sorted.length) {
        this.shippingRates = null;
        this.showShippingMessage('Não há opções de entrega para esse CEP.', true);
        return;
      }
      this.shippingRates = sorted;

      // Quote only: the options are listed, never added to the cart total.
      const list = document.createElement('ul');
      list.className = 'ss-ship__rates';
      list.setAttribute('role', 'list');
      sorted.forEach((rate) => {
        const item = document.createElement('li');
        item.className = 'ss-ship__rate';
        const info = document.createElement('span');
        info.className = 'ss-ship__rate-info';
        const name = document.createElement('span');
        name.className = 'ss-ship__rate-name';
        name.textContent = rate.name;
        const eta = document.createElement('span');
        eta.className = 'ss-ship__rate-eta';
        eta.textContent = this.deliveryText(rate.days);
        info.append(name, eta);
        const price = document.createElement('span');
        price.className = `ss-ship__rate-price${rate.cents === 0 ? ' is-free' : ''}`;
        price.textContent = rate.cents === 0 ? 'Grátis' : this.formatMoney(rate.cents);
        item.append(info, price);
        list.append(item);
      });

      result.replaceChildren(list);
      if (note) note.hidden = false;
    }

    deliveryText(days) {
      const [min, max] = days;
      if (!min && !max) return '';
      if (!max || min === max) return `${min || max} ${(min || max) === 1 ? 'dia útil' : 'dias úteis'}`;
      return `${min} a ${max} dias úteis`;
    }

    showShippingMessage(text, isError) {
      const result = this.querySelector('[data-ss-ship-result]');
      if (!result) return;
      const message = document.createElement('p');
      message.className = `ss-ship__message${isError ? ' is-error' : ''}`;
      message.textContent = text;
      result.replaceChildren(message);
    }

    formatMoney(cents) {
      if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
      return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
  }

  customElements.define('ss-cart-drawer', SsCartDrawer);
})();
